import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";

import {
  buildLeaveWorkingDaysCall,
  computeLeaveAdjustmentImpact,
  excludeSundays,
  filterImpactToWorkingDays,
  parseWorkingDaysPayload,
  validateLeaveAdjustment,
} from "./leave-request-helpers";

type Props = {
  open: boolean;
  submitting: boolean;
  startDate: string;
  endDate: string;
  onClose: () => void;
  onSubmit: (dates: { startDate: string; endDate: string }, reason: string) => void;
};

/** Day keys are plain calendar dates, so format them as local dates (no UTC shift). */
function formatDays(days: string[]): string {
  return days.map((d) => formatListDate(d, { empty: "—" })).join(", ");
}

/** Ask to shorten or extend an approved leave; nothing changes until an approver accepts. */
export function AdjustLeaveDialog({
  open,
  submitting,
  startDate,
  endDate,
  onClose,
  onSubmit,
}: Props) {
  const [newStart, setNewStart] = useState(startDate.slice(0, 10));
  const [newEnd, setNewEnd] = useState(endDate.slice(0, 10));
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setNewStart(startDate.slice(0, 10));
      setNewEnd(endDate.slice(0, 10));
      setReason("");
      setTouched(false);
    }
  }, [open, startDate, endDate]);

  const error = validateLeaveAdjustment({
    oldStart: startDate,
    oldEnd: endDate,
    newStart: newStart || null,
    newEnd: newEnd || null,
    reason,
  });

  const calendarImpact = useMemo(
    () =>
      newStart && newEnd && newStart <= newEnd
        ? computeLeaveAdjustmentImpact(startDate, endDate, newStart, newEnd)
        : { removed: [], added: [] },
    [startDate, endDate, newStart, newEnd]
  );

  // Working days (Sundays + office holidays) come from the API; null while loading / on failure.
  const [workingDays, setWorkingDays] = useState<string[] | null>(null);
  const changed = [...calendarImpact.removed, ...calendarImpact.added].sort();
  const rangeFrom = changed[0];
  const rangeTo = changed[changed.length - 1];

  useEffect(() => {
    if (!open || !rangeFrom || !rangeTo) {
      setWorkingDays(null);
      return;
    }
    let cancelled = false;
    setWorkingDays(null);
    callApi(buildLeaveWorkingDaysCall(rangeFrom, rangeTo))
      .then((res) => {
        if (!cancelled) setWorkingDays(parseWorkingDaysPayload(res.data));
      })
      .catch(() => {
        if (!cancelled) setWorkingDays(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rangeFrom, rangeTo]);

  // Fallback while loading / if the call fails: Sundays only (holidays unknown).
  const impact = workingDays
    ? filterImpactToWorkingDays(calendarImpact, workingDays)
    : {
        removed: excludeSundays(calendarImpact.removed),
        added: excludeSundays(calendarImpact.added),
      };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Adjust approved leave</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Currently {formatListDate(startDate, { empty: "—" })} –{" "}
          {formatListDate(endDate, { empty: "—" })}. The approved dates stay in force until an
          approver accepts the change.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            label="New start"
            type="date"
            value={newStart}
            onChange={(e) => setNewStart(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            disabled={submitting}
            sx={{ flex: 1, minWidth: 160 }}
          />
          <TextField
            label="New end"
            type="date"
            value={newEnd}
            onChange={(e) => setNewEnd(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            size="small"
            disabled={submitting}
            sx={{ flex: 1, minWidth: 160 }}
          />
        </Box>
        <TextField
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          disabled={submitting}
          sx={{ mb: 2 }}
        />
        {impact.removed.length > 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Frees {impact.removed.length} working day(s): {formatDays(impact.removed)}. Attendance
            on those days goes back to normal and paid balance is restored once approved.
          </Alert>
        ) : null}
        {impact.added.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Adds {impact.added.length} working day(s): {formatDays(impact.added)}. Needs approval
            and may use paid balance. Days with recorded attendance keep their attendance.
          </Alert>
        ) : null}
        {touched && error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          disabled={submitting}
          onClick={() => {
            setTouched(true);
            if (error) return;
            onSubmit({ startDate: newStart, endDate: newEnd }, reason);
          }}
        >
          Request adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
